import {
  black,
  blue,
  brown,
  grey,
  red,
  white,
  yellow,
} from "@/modules/theme/utils/colors";
import { fade } from "@/modules/theme/utils/fade";

export const spacingUnitPxValue = 8;

export function createTheme() {
  return {
    spacing: createSpacing(spacingUnitPxValue),
    breakpoints: {
      up: {
        sm: `@media (min-width: ${breakpointValues.sm}px)`,
        md: `@media (min-width: ${breakpointValues.md}px)`,
        lg: `@media (min-width: ${breakpointValues.lg}px)`,
        xl: `@media (min-width: ${breakpointValues.xl}px)`,
      },
      down: {
        sm: `@media (max-width: ${breakpointValues.sm - 1}.97px)`,
        md: `@media (max-width: ${breakpointValues.md - 1}.97px)`,
        lg: `@media (max-width: ${breakpointValues.lg - 1}.97px)`,
        xl: `@media (max-width: ${breakpointValues.xl - 1}.97px)`,
      },
    },
    viewportHeight: {
      up: {
        md: `@media (min-height: ${viewportHeightValues.md}px)`,
      },
      down: {
        md: `@media (max-height: ${viewportHeightValues.md - 1}.97px)`,
      },
    },
    palette: {
      default: {
        background: white,
        text: grey[900],
      },
      gradient: {
        testSuccess: `linear-gradient(to bottom, ${white} 32%, ${yellow[100]} 99%);`,
        testFailed: `linear-gradient(to bottom, ${white} 32%, ${blue[200]} 100%);`,
      },
      primary: {
        light: yellow[400],
        main: yellow[600],
        dark: yellow[700],
        text: brown[600],
        textDark: brown[800],
      },
      secondary: {
        light: grey[200],
        main: grey[300],
        dark: grey[400],
        text: grey[800],
        textDark: grey[900],
      },
      tertiary: {
        light: blue[100],
        main: blue[500],
        dark: blue[500],
        text: blue[500],
        textDark: blue[500],
      },
      disabled: {
        background: grey[250],
        text: grey[450],
      },
      error: {
        light: red[100],
        main: red[400],
        dark: red[400],
        text: red[500],
        textLight: red[400],
        textDark: red[700],
      },
    },
    typography: {
      pxToRem,
      fonts: {
        body: "'Montserrat', sans-serif",
        content: "'Lato', sans-serif",
      },
      fontWeight: {
        thin: 100,
        normal: 400,
        medium: 500,
        semiBold: 600,
        bold: 700,
      },
      fontSize: {
        html: htmlFontSize,
        large: pxToRem(16),
        default: pxToRem(14),
        small: pxToRem(12),
        extraSmall: pxToRem(10),
      },
    },
    zIndexes: {
      dropdown: 1,
      navMenu: 2,
      tooltip: 3,
      topbar: 4,
      backdrop: 5,
      dialog: 6,
    },
    boxShadows: {
      modal: `0 5px 10px 0 ${fade(black, 0.24)}`,
      navMenu: `0 0 20px 0 ${fade(black, 0.1)}`,
      button: `0 5px 20px 0 ${fade(yellow[400], 0.38)}`,
      dropdown: `0 2px 4px 0 ${fade(grey[500], 0.44)}`,
      task: `0 0 30px 0 ${fade(black, 0.06)}`,
      checkbox: `0 2px 10px 0 ${fade(yellow[400], 0.45)}`,
      answerRadio: `0 5px 10px 0 ${fade(yellow[400], 0.48)}`,
      snackbar: `0 5px 10px 0 ${fade(grey[800], 0.2)}`,
      taskDone: `5px 0 5px 0 ${fade(yellow[400], 0.19)}`,
    },
    borders: {
      input: {
        main: `1px solid ${grey[300]}`,
        error: `1px solid ${red[400]}`,
        secondary: `1px solid ${grey[300]}`,
        tertiary: `1px solid ${blue[500]}`,
      },
    },
    borderRadius: {
      default: "3px",
      medium: "5px",
      checkbox: "4px",
      large: "10px",
      checkboxRadio: "12px",
      navMenu: "20px",
      huge: "30px",
      round: "50%",
    },
    backdrops: {
      default: fade(black, 0.3),
    },
  };
}

export const breakpointValues = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};

const viewportHeightValues = {
  md: 650,
};

const htmlFontSize = 16;

const pxToRem = (px: number) => `${px / htmlFontSize}rem`;

function createSpacing(base: number) {
  return (indices: number | number[] = 1): string => {
    if (!Array.isArray(indices)) {
      indices = [indices];
    }

    return indices
      .map((index) => index * base)
      .map((px) => `${px}px`)
      .join(" ");
  };
}

export type Theme = ReturnType<typeof createTheme>;
